import React, { FC } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { keyframes, styled } from '@stitches/react';
import Icon from '@components/atoms/icon';

const overlayShow = keyframes({
  from: {
    opacity: 0
  },
  to: {
    opacity: 1
  }
});

const StyledOverlay = styled(Dialog.Overlay, {
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  position: 'fixed',
  inset: 0,
  animation: `${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  backdropFilter: 'blur(4px)',
  zIndex: 1000
});

const contentShow = keyframes({
  from: {
    opacity: 0,
    transform: 'translate(-50%, -48%) scale(0.96)'
  },
  to: {
    opacity: 1,
    transform: 'translate(-50%, -50%) scale(1)'
  }
});

// A bottom sheet is positioned with `top-full -translate-y-full`, so its
// journey is translateY(0) (just below the viewport) up to -100%. The centred
// keyframes must not run here: while an animation runs its transform
// overrides the positioning classes, which used to drag the sheet in from the
// bottom-left corner and snap it into place when the animation finished.
const sheetShow = keyframes({
  from: {
    transform: 'translateY(0)'
  },
  to: {
    transform: 'translateY(-100%)'
  }
});

const StyledContent = styled(Dialog.Content, {
  backgroundColor: 'white',
  boxShadow: '0px 2px 25px rgba(0, 0, 0, 0.15)',
  position: 'fixed',
  maxHeight: '95vh',
  animation: `${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  zIndex: 10000,
  variants: {
    isBottomSheet: {
      true: {
        animation: `${sheetShow} 200ms cubic-bezier(0.16, 1, 0.3, 1)`
      }
    }
  }
});

type FFHModalType = {
  open: boolean;
  handleClose: () => void;
  childrenClassName?: string;
  width?: string;
  bottom?: boolean;
  iconColor?: string;
  children: React.ReactNode;
  title?: string;
  allowOverflow?: boolean;
  /**
   * Suppresses the absolutely-positioned close button so a modal that draws
   * its own header can place the control inline. Such a modal owns the
   * `close-modal-button` hook instead.
   */
  hideCloseButton?: boolean;
};

const FFHModal: FC<FFHModalType> = ({
  children,
  open,
  handleClose,
  childrenClassName = '',
  bottom = false,
  iconColor = 'text-neutral-800',
  title = 'Modal Dialog',
  allowOverflow = false,
  hideCloseButton = false
}) => (
  <Dialog.Root
    open={open}
    onOpenChange={(isOpen) => {
      if (!isOpen) {
        handleClose();
      }
    }}
  >
    <Dialog.Portal>
      <StyledOverlay />
      <StyledContent
        isBottomSheet={bottom}
        className={`${
          allowOverflow ? 'overflow-y-visible' : 'overflow-y-scroll'
        } scrollbar-hide relative ${
          bottom
            ? 'top-full left-0 -translate-y-full -translate-x-0 w-full'
            : 'rounded-lg top-2/4 left-2/4 -translate-y-1/2 -translate-x-1/2'
        } ${childrenClassName}`}
      >
        {/* No id override: Radix wires aria-labelledby to its own generated
            title id, and its missing-title check looks that id up. */}
        <VisuallyHidden>
          <Dialog.Title>{title}</Dialog.Title>
        </VisuallyHidden>
        {!hideCloseButton && (
          <div
            onClick={handleClose}
            data-cy="close-modal-button"
            className="pointer p-2 absolute top-0 right-1 text-gray-400 z-50"
          >
            <Icon name="Close" className={`${iconColor} w-5 h-5 text-lg`} />
          </div>
        )}
        {children}
      </StyledContent>
    </Dialog.Portal>
  </Dialog.Root>
);

export default FFHModal;
